import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { t } from "@/lib/i18n";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [done, setDone] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError(t("The two passwords don't match."));
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password });
      setDone(data.message);
    } catch (e2) {
      setError(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="min-h-[calc(100vh-4rem)] flex items-center justify-center p-6 sm:p-12">
      <div className="w-full max-w-md fade-up" data-testid="reset-password-page">
        <p className="eyebrow">{t("Reset password")}</p>
        <h1 className="font-serif text-4xl mt-2 text-[#1C201F] leading-tight">{t("Choose a new password.")}</h1>
        {!token ? (
          <div className="mt-8 text-sm text-[#9B2C2C] bg-[#FCEDED] border border-[#F5C2C2] rounded-lg px-3 py-2"
            data-testid="reset-password-missing-token">
            {t("This reset link is incomplete. Please request a new one.")}
          </div>
        ) : done ? (
          <div className="mt-8 space-y-4">
            <div className="text-sm text-[#2F524A] bg-[#F4F7F6] border border-[#E8ECEA] rounded-lg px-4 py-3"
              data-testid="reset-password-success">{done}</div>
            <Link to="/login" className="btn-primary inline-block !rounded-xl" data-testid="reset-password-login">
              {t("Log in")}
            </Link>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="mt-8 space-y-4">
            <div>
              <label className="eyebrow block mb-2" htmlFor="new-password">{t("New password")}</label>
              <input id="new-password" type="password" required minLength={6} value={password}
                onChange={(e) => setPassword(e.target.value)} className="input-field"
                placeholder={t("At least 6 characters")} autoComplete="new-password" data-testid="reset-password-input" />
            </div>
            <div>
              <label className="eyebrow block mb-2" htmlFor="confirm-password">{t("Confirm new password")}</label>
              <input id="confirm-password" type="password" required minLength={6} value={confirm}
                onChange={(e) => setConfirm(e.target.value)} className="input-field"
                autoComplete="new-password" data-testid="reset-password-confirm" />
            </div>
            {error && (
              <div className="text-sm text-[#9B2C2C] bg-[#FCEDED] border border-[#F5C2C2] rounded-lg px-3 py-2"
                data-testid="reset-password-error">{error}</div>
            )}
            <button type="submit" disabled={loading} className="btn-primary w-full !rounded-xl"
              data-testid="reset-password-submit">
              {loading ? t("Saving…") : t("Save new password")}
            </button>
          </form>
        )}
        <p className="mt-6 text-sm text-[#5C6661]">
          <Link to="/forgot-password" className="text-[#2F524A] font-semibold hover:underline"
            data-testid="reset-password-request-new">
            {t("Request a new link")}
          </Link>
        </p>
      </div>
    </section>
  );
}
